/**
 * media-gallery-v2.js — <media-gallery-v2>
 *
 * Desktop  — FoxTheme.Carousel (bundled Swiper) for carousel layouts;
 *            stacked / grid layouts use plain show / hide filtering.
 *
 * Mobile   — Swiper 11 UMD loaded once from CDN via dynamic script injection,
 *            then available as window.Swiper for all instances.
 *            The DOM is pre-filtered BEFORE Swiper initialises so no
 *            post-init DOM manipulation is ever needed.  On variant change:
 *            destroy → filter DOM → reinit.
 *
 * Variant filtering rules (both breakpoints):
 *   - alt == ""           → always shown (untagged / lifestyle image)
 *   - alt == variantTitle → shown for that variant
 *   - alt == other value  → hidden
 *   - no images tagged    → show all (fallback)
 */

const SWIPER_CDN_JS  = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
const SWIPER_CDN_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
const MOBILE_MQ      = window.matchMedia('(max-width: 767px)');

// Load Swiper script once, return a promise that resolves when window.Swiper is ready.
let _swiperLoadPromise = null;
function loadSwiper() {
  if (_swiperLoadPromise) return _swiperLoadPromise;
  _swiperLoadPromise = new Promise((resolve, reject) => {
    if (window.Swiper) { resolve(); return; }

    // Inject CSS only once.
    if (!document.querySelector(`link[href="${SWIPER_CDN_CSS}"]`)) {
      const link = Object.assign(document.createElement('link'), {
        rel: 'stylesheet', href: SWIPER_CDN_CSS,
      });
      document.head.appendChild(link);
    }

    // Inject script only once.
    const existing = document.querySelector(`script[src="${SWIPER_CDN_JS}"]`);
    if (existing) {
      // Script tag exists but may still be loading — poll for window.Swiper.
      const id = setInterval(() => { if (window.Swiper) { clearInterval(id); resolve(); } }, 50);
      return;
    }

    const script = Object.assign(document.createElement('script'), { src: SWIPER_CDN_JS });
    script.onload  = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return _swiperLoadPromise;
}

if (!customElements.get('media-gallery-v2')) {
  customElements.define(
    'media-gallery-v2',
    class MediaGalleryV2 extends HTMLElement {

      // ── Lifecycle ────────────────────────────────────────────────────────

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      disconnectedCallback() {
        this._unsubscribeVariant?.();
        this._destroyMobileSwiper();
        this._destroyDesktopSlider();
        MOBILE_MQ.removeEventListener('change', this._onBreakpoint);
      }

      // ── Init ─────────────────────────────────────────────────────────────

      init() {
        // Snapshot original DOM nodes once — these are the source of truth for
        // every filter operation.  Never modify this._all* arrays.
        this._allMobileSlides  = [...this.querySelectorAll('.product__media-slide')];
        this._allMobileThumbs  = [...this.querySelectorAll('.product__media-thumbs-v2 .product__thumbs-item')];
        this._allDesktopSlides = [...this.querySelectorAll('.product__media-gallery-desktop .product__media-item')];
        this._allDesktopThumbs = [...this.querySelectorAll('.product__media-gallery-desktop .product__thumbs-item')];

        this._mobileSwiper  = null;
        this._mobileThumbs  = null;
        this._desktopSlider = null;
        this._desktopThumbs = null;

        // Pre-filter mobile DOM BEFORE Swiper ever sees it so no post-init
        // manipulation is needed.
        const initialOpts = this._parseVariantOptions();
        if (initialOpts) this._rebuildMobileDom(initialOpts);

        // Init the correct layout for the current breakpoint.
        this._onBreakpoint = this._handleBreakpoint.bind(this);
        MOBILE_MQ.addEventListener('change', this._onBreakpoint);
        this._handleBreakpoint();

        // Desktop initial filter (show / hide).
        if (initialOpts && !MOBILE_MQ.matches) {
          this._filterDesktopSlides(initialOpts);
        }

        // Subscribe to Shopify variant changes.
        this._unsubscribeVariant = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          ({ data: { sectionId, variant } }) => {
            if (sectionId !== this.dataset.section || !variant?.options) return;
            this._applyFilter(variant.options, variant);
          }
        );
      }

      _parseVariantOptions() {
        try {
          const opts = JSON.parse(this.dataset.initialVariantOptions || '[]');
          return opts.length ? opts.map(o => o.trim().toLowerCase()).join(' / ') : null;
        } catch (_) { return null; }
      }

      // ── Breakpoint ───────────────────────────────────────────────────────

      _handleBreakpoint() {
        if (MOBILE_MQ.matches) {
          this._destroyDesktopSlider();
          this._initMobileSwiper();
        } else {
          this._destroyMobileSwiper();
          this._initDesktopSlider();
        }
      }

      // ── Mobile DOM filtering ─────────────────────────────────────────────

      /**
       * Rebuild the mobile swiper-wrapper and thumbs-wrapper with only the
       * slides matching variantTitle.  Cleans Swiper-added attributes so the
       * next Swiper init always starts from a pristine state.
       */
      _rebuildMobileDom(variantTitle) {
        const hasTagged = this._allMobileSlides.some(
          s => s.dataset.mediaAlt && s.dataset.mediaAlt === variantTitle
        );

        const visible = hasTagged
          ? this._allMobileSlides.filter(s => !s.dataset.mediaAlt || s.dataset.mediaAlt === variantTitle)
          : [...this._allMobileSlides];

        this._replaceWrapperChildren(
          this.querySelector('.product__media-swiper-v2 .swiper-wrapper'),
          visible
        );

        // Thumbnails: keep only thumbs whose target media ID is visible.
        const visibleIds = new Set(visible.map(s => s.dataset.mediaId));
        const visibleThumbs = this._allMobileThumbs.filter(t => visibleIds.has(t.dataset.target));

        this._replaceWrapperChildren(
          this.querySelector('.product__media-thumbs-v2 .swiper-wrapper'),
          visibleThumbs
        );
      }

      /** Move `nodes` into `wrapper`, stripping Swiper runtime attributes. */
      _replaceWrapperChildren(wrapper, nodes) {
        if (!wrapper) return;
        const frag = document.createDocumentFragment();
        nodes.forEach(n => {
          n.removeAttribute('style');
          n.className = n.className
            .split(' ')
            .filter(c => !c.startsWith('swiper-slide-') && c !== 'swiper-slide-visible')
            .join(' ');
          frag.appendChild(n);
        });
        wrapper.innerHTML = '';
        wrapper.appendChild(frag);
      }

      // ── Mobile Swiper (CDN) ──────────────────────────────────────────────

      async _initMobileSwiper() {
        if (this._mobileSwiper) return;

        const container = this.querySelector('.product__media-swiper-v2');
        const thumbsEl  = this.querySelector('.product__media-thumbs-v2');
        if (!container) return;

        await loadSwiper();

        // Guard: breakpoint may have changed while the script was loading.
        if (!MOBILE_MQ.matches || this._mobileSwiper) return;

        // Init thumbnails first so we can reference it in the main Swiper.
        if (thumbsEl && thumbsEl.querySelector('.swiper-slide')) {
          this._mobileThumbs = new window.Swiper(thumbsEl, {
            slidesPerView:       4,
            spaceBetween:        8,
            watchSlidesProgress: true,
            threshold:           2,
          });
        }

        this._mobileSwiper = new window.Swiper(container, {
          slidesPerView: 1,
          spaceBetween:  16,
          loop:          false,
          grabCursor:    true,
          threshold:     2,
          navigation: {
            nextEl: container.querySelector('.swiper-button-next'),
            prevEl: container.querySelector('.swiper-button-prev'),
          },
          pagination: this._mobileThumbs ? false : {
            el:        container.querySelector('.product__media-pagination-v2'),
            type:      'fraction',
            clickable: true,
          },
          thumbs: this._mobileThumbs
            ? { swiper: this._mobileThumbs, autoScrollOffset: 1 }
            : undefined,
        });
      }

      _destroyMobileSwiper() {
        this._mobileThumbs?.destroy(true, true);
        this._mobileThumbs = null;
        this._mobileSwiper?.destroy(true, true);
        this._mobileSwiper = null;
      }

      // ── Desktop slider (FoxTheme.Carousel) ───────────────────────────────

      _initDesktopSlider() {
        if (this.dataset.enableDesktopSlider !== 'true' || this._desktopSlider) return;

        const viewer   = this.querySelector('[id^="GalleryViewer"]');
        const thumbsEl = this.querySelector('[id^="GalleryThumbnails"]');
        if (!viewer) return;

        const gap = parseInt(this.dataset.mediaItemGap) || 28;

        if (thumbsEl) {
          this._desktopThumbs = new FoxTheme.Carousel(thumbsEl, {
            slidesPerView: 4,
            spaceBetween:  10,
            freeMode:      true,
            watchSlidesProgress: true,
            breakpoints: {
              461: { slidesPerView: 5, spaceBetween: gap },
              768: this.dataset.mediaLayout === 'vertical-carousel'
                    ? { direction: 'vertical', slidesPerView: 'auto' }
                    : {},
            },
          }, []);
          this._desktopThumbs.init();
        }

        const opts = {
          init:          false,
          slidesPerView: this.dataset.mediaLayout === 'slider-freemode' ? 'auto' : 1,
          spaceBetween:  gap,
          loop:          false,
          grabCursor:    true,
          autoHeight:    true,
          threshold:     2,
          navigation: {
            nextEl: this.querySelector('.product__media-gallery-desktop .swiper-button-next'),
            prevEl: this.querySelector('.product__media-gallery-desktop .swiper-button-prev'),
          },
          pagination: {
            el:        this.querySelector('.product__media-gallery-desktop .swiper-pagination'),
            type:      'fraction',
            clickable: true,
          },
        };

        if (this._desktopThumbs) {
          opts.thumbs = { swiper: this._desktopThumbs.slider, autoScrollOffset: 2 };
        }

        this._desktopSlider = new FoxTheme.Carousel(viewer, opts, [FoxTheme.Swiper.Thumbs]);
        this._desktopSlider.init();
        this._desktopSlider.slider.init();
      }

      _destroyDesktopSlider() {
        if (this._desktopSlider) {
          this._desktopSlider.slider.destroy();
          this._desktopSlider = null;
        }
      }

      _filterDesktopSlides(variantTitle, variant = null) {
        const hasTagged = this._allDesktopSlides.some(
          s => (s.dataset.mediaAlt || '').trim().toLowerCase() === variantTitle
        );

        if (this._desktopSlider?.slider && variant?.featured_media) {
          const idx = this._allDesktopSlides.findIndex(
            s => s.dataset.mediaId == variant.featured_media.id
          );
          if (idx >= 0) this._desktopSlider.slider.slideTo(idx, 0, false);
          return;
        }

        const visibleIds = new Set();
        this._allDesktopSlides.forEach(slide => {
          const alt     = (slide.dataset.mediaAlt || '').trim().toLowerCase();
          const show    = !hasTagged || !alt || alt === variantTitle;
          slide.style.display = show ? '' : 'none';
          if (show) visibleIds.add(slide.dataset.mediaId);
        });

        this._allDesktopThumbs.forEach(t => {
          t.style.display = visibleIds.has(t.dataset.target) ? '' : 'none';
        });

        this._updateDesktopMainItemClasses(
          this._allDesktopSlides.filter(s => s.style.display !== 'none')
        );
      }

      _updateDesktopMainItemClasses(visibleItems) {
        const layout = this.dataset.mediaLayout;
        if (!['stacked', 'grid-mix', 'grid-1st-full-2cols'].includes(layout)) return;

        visibleItems.forEach(item => item.classList.remove('product__media-item--main'));
        visibleItems.forEach((item, i) => {
          const isMain =
            layout === 'stacked' ||
            (layout === 'grid-mix'            && i % 3 === 0) ||
            (layout === 'grid-1st-full-2cols' && i === 0);
          if (isMain) item.classList.add('product__media-item--main');
        });
      }

      // ── Unified filter entry ─────────────────────────────────────────────

      _applyFilter(variantOptions, variant = null) {
        const variantTitle = variantOptions.map(o => o.trim().toLowerCase()).join(' / ');

        if (MOBILE_MQ.matches) {
          // Destroy → rebuild filtered DOM → reinit.  Swiper always inits
          // on a clean, pre-filtered DOM — no post-init manipulation needed.
          this._destroyMobileSwiper();
          this._rebuildMobileDom(variantTitle);
          this._initMobileSwiper();
        } else {
          this._filterDesktopSlides(variantTitle, variant);
        }
      }
    }
  );
}
